import React, { type FC, type KeyboardEvent, type ReactNode, type SyntheticEvent, useId, useRef, useState } from 'react'
import { Menu, MenuItem, type SvgIconProps, type SxProps, type Theme } from '@mui/material'

import { extractDataTestId } from '../utils'
import { DotsThreeIcon } from './Branding'
import { IconButton } from './Button'

export type ActionMenuItem = Readonly<{
    key: string
    label: ReactNode
    icon?: FC<SvgIconProps>
    destructive?: boolean
    disabled?: boolean
    onClick: VoidFunction
}>

type Props = Readonly<{
    /** The accessible name of the trigger, e.g. "Actions for opcua", named after the row id */
    label: string
    items: readonly ActionMenuItem[]
}>

/** `700`, as on the toolbar's `dangerOutline` Delete: `600` falls below AA contrast on the hovered item's grey background. The icon inherits it */
const destructiveItemStyle: SxProps<Theme> = { color: 'error.700' }

const ACTIVATION_KEYS: readonly string[] = ['Enter', ' ']

/**
 * Lives inside clickable containers such as table rows, which open on click and Enter, and React events
 * bubble through the menu's portal - so those stop here instead of also activating the container
 */
const stopPropagation = (e: SyntheticEvent): void => e.stopPropagation()
const stopActivationKeyPropagation = (e: KeyboardEvent): void => {
    if (ACTIVATION_KEYS.includes(e.key)) {
        e.stopPropagation()
    }
}

export const ActionMenu: FC<Props> = ({ label, items, ...props }) => {
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [open, setOpen] = useState(false)
    const triggerId = useId()
    const menuId = useId()
    const testId = extractDataTestId(props) || 'action-menu'

    const select = (item: ActionMenuItem): void => {
        /** A disabled item blocks the pointer through its style only, so a click dispatched to it still arrives */
        if (item.disabled) {
            return
        }

        setOpen(false)
        item.onClick()
    }

    return (
        <>
            <IconButton
                ref={buttonRef}
                id={triggerId}
                data-testid={`${testId}-button`}
                variant="ghost"
                size="medium"
                aria-label={label}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                onClick={(e) => {
                    e.stopPropagation()
                    setOpen(true)
                }}
                onKeyDown={stopActivationKeyPropagation}
            >
                <DotsThreeIcon />
            </IconButton>
            <Menu
                data-testid={`${testId}-menu`}
                anchorEl={buttonRef.current}
                open={open}
                onClose={() => setOpen(false)}
                onClick={stopPropagation}
                onKeyDown={stopActivationKeyPropagation}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ list: { id: menuId, 'aria-labelledby': triggerId } }}
            >
                {items.map((item) => (
                    <MenuItem
                        key={item.key}
                        data-testid={`${testId}-item-${item.key}`}
                        sx={item.destructive ? destructiveItemStyle : undefined}
                        disabled={item.disabled}
                        onClick={() => select(item)}
                    >
                        {item.icon && <item.icon />}
                        {item.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}
